import { defineStore } from 'pinia'
import { ref } from 'vue'

import {
  ExtractionPublicationService,
  WorkbooksService,
  OpenAPI,
  triggerBlobDownload,
  type FilePatch,
  type FileResourceOut,
  type ScopeOut,
  type WorkbookSummaryOut,
} from '@/api'
import { classifyApiError, type NormalizedApiError } from '@/utils/apiError'

const FILE_PAGE_SIZE = 50
const STANDARD_SCOPE_DIMENSIONS: readonly string[] = ['mine', 'level', 'vein']

export function sourceFileContentUrl(projectId: number, fileId: string): string {
  const base = OpenAPI.BASE.replace(/\/$/, '')
  return `${base}/v1/projects/${projectId}/files/${encodeURIComponent(fileId)}/content`
}

export function sourceScopeLabel(values: Record<string, string>): string {
  const primary = STANDARD_SCOPE_DIMENSIONS.map((dimension) => values[dimension]).filter(Boolean)
  const remaining = Object.keys(values)
    .filter((dimension) => !STANDARD_SCOPE_DIMENSIONS.includes(dimension))
    .sort()
    .map((dimension) => values[dimension])
    .filter(Boolean)
  return [...primary, ...remaining].join(' · ') || 'Unscoped'
}

export interface SourceFileGroup {
  id: string
  label: string
  files: FileResourceOut[]
}

export function isCanonicalSourcePdf(file: FileResourceOut): boolean {
  return file.scope_id !== null && file.media_type === 'application/pdf'
}

export function groupSourceFiles(files: FileResourceOut[], scopes: ScopeOut[]): SourceFileGroup[] {
  const groups = new Map<string, SourceFileGroup>()
  const scopesById = new Map(scopes.map((scope) => [scope.id, scope]))
  for (const file of files) {
    const id = file.scope_id ?? 'unscoped'
    const scope = file.scope_id ? scopesById.get(file.scope_id) : null
    const label = sourceScopeLabel(scope?.values ?? file.scope_values)
    const group = groups.get(id) ?? { id, label, files: [] }
    group.files.push(file)
    groups.set(id, group)
  }
  // Preserve server order so appending a page never moves existing rows.
  return [...groups.values()]
}

function normalizedError(error: unknown): NormalizedApiError {
  return classifyApiError(error)
}

function unsupportedSourceFileError(): NormalizedApiError {
  return {
    status: 404,
    code: 'file_not_found',
    message: "The source PDF you're looking for wasn't found.",
    technical: 'The requested file is not a canonical source PDF.',
    kind: 'user',
  }
}

function requireBlob(value: unknown): Blob {
  if (value instanceof Blob) return value
  throw new Error('The source PDF response was not a downloadable file. Reload and try again.')
}

export const useSourceFilesStore = defineStore('sourceFiles', () => {
  const activeProjectId = ref<number | null>(null)
  const files = ref<FileResourceOut[]>([])
  const totalFiles = ref<number | null>(null)
  const scopes = ref<ScopeOut[]>([])
  const selectedFile = ref<FileResourceOut | null>(null)
  const scopeWorkbook = ref<WorkbookSummaryOut | null>(null)
  const workbookLoading = ref(false)
  const workbookError = ref<NormalizedApiError | null>(null)
  const listLoading = ref(false)
  const hasMoreFiles = ref(false)
  const scopesLoading = ref(false)
  const detailLoading = ref(false)
  const mutationLoading = ref(false)
  const downloadingFileId = ref<string | null>(null)
  const listError = ref<NormalizedApiError | null>(null)
  const scopesError = ref<NormalizedApiError | null>(null)
  const detailError = ref<NormalizedApiError | null>(null)
  const mutationError = ref<NormalizedApiError | null>(null)
  const downloadError = ref<NormalizedApiError | null>(null)

  let workspaceGeneration = 0
  let detailGeneration = 0
  let mutationGeneration = 0
  let downloadGeneration = 0
  let listOffset = 0
  let listFilters: { q?: string; scopeId?: string } = {}
  let workbookGeneration = 0
  let includeSelectedWorkbook = false
  let selectedWorkbookCustomerVisible = false

  function clearWorkbook(): void {
    workbookGeneration += 1
    scopeWorkbook.value = null
    workbookLoading.value = false
    workbookError.value = null
  }

  async function loadSelectedWorkbook(): Promise<void> {
    clearWorkbook()
    const file = selectedFile.value
    if (!includeSelectedWorkbook || !file?.scope_id) return
    const generation = workbookGeneration
    workbookLoading.value = true
    try {
      const result = await WorkbooksService.listWorkbooksProjectsProjectIdWorkbooksGet({
        projectId: file.project_id,
        scopeId: file.scope_id,
        pageSize: 1,
        ...(selectedWorkbookCustomerVisible ? { review: 'customer_visible' as const } : {}),
      })
      if (generation === workbookGeneration) scopeWorkbook.value = result.items[0] ?? null
    } catch (error) {
      if (generation === workbookGeneration) workbookError.value = normalizedError(error)
    } finally {
      if (generation === workbookGeneration) workbookLoading.value = false
    }
  }

  function beginProject(projectId: number): void {
    if (activeProjectId.value === projectId) return
    activeProjectId.value = projectId
    resetProjectState()
  }

  function resetProjectState(): void {
    workspaceGeneration += 1
    detailGeneration += 1
    mutationGeneration += 1
    downloadGeneration += 1
    files.value = []
    totalFiles.value = null
    scopes.value = []
    selectedFile.value = null
    clearWorkbook()
    includeSelectedWorkbook = false
    listLoading.value = false
    hasMoreFiles.value = false
    listOffset = 0
    listFilters = {}
    scopesLoading.value = false
    detailLoading.value = false
    mutationLoading.value = false
    downloadingFileId.value = null
    listError.value = null
    scopesError.value = null
    detailError.value = null
    mutationError.value = null
    downloadError.value = null
  }

  function replaceResource(resource: FileResourceOut): void {
    const index = files.value.findIndex((file) => file.id === resource.id)
    if (index >= 0 && files.value[index].revision <= resource.revision) {
      files.value.splice(index, 1, resource)
    }
    if (
      selectedFile.value?.id === resource.id &&
      selectedFile.value.revision <= resource.revision
    ) {
      const scopeChanged = selectedFile.value.scope_id !== resource.scope_id
      selectedFile.value = resource
      if (scopeChanged) void loadSelectedWorkbook()
    }
  }

  async function fetchWorkspace(
    projectId: number,
    filters: { q?: string; scopeId?: string } = {},
  ): Promise<void> {
    beginProject(projectId)
    const generation = ++workspaceGeneration
    listLoading.value = false
    hasMoreFiles.value = true
    listOffset = 0
    listFilters = { ...filters }
    scopesLoading.value = true
    files.value = []
    totalFiles.value = null
    listError.value = null
    scopesError.value = null

    await Promise.all([loadMoreFiles(), fetchScopes(projectId, generation)])
  }

  async function fetchScopes(projectId: number, generation: number): Promise<void> {
    try {
      const result =
        await ExtractionPublicationService.listProjectScopesV1ProjectsProjectIdScopesGet({
          projectId,
        })
      if (generation === workspaceGeneration && activeProjectId.value === projectId)
        scopes.value = result
    } catch (error) {
      if (generation === workspaceGeneration && activeProjectId.value === projectId)
        scopesError.value = normalizedError(error)
    } finally {
      if (generation === workspaceGeneration && activeProjectId.value === projectId)
        scopesLoading.value = false
    }
  }

  async function loadMoreFiles(): Promise<void> {
    const projectId = activeProjectId.value
    if (projectId === null || listLoading.value || !hasMoreFiles.value) return
    const generation = workspaceGeneration
    listLoading.value = true
    listError.value = null
    try {
      const page = await ExtractionPublicationService.listFilesV1ProjectsProjectIdFilesGet({
        projectId,
        q: listFilters.q,
        scopeId: listFilters.scopeId,
        sourceOnly: true,
        includeTotal: true,
        limit: FILE_PAGE_SIZE,
        offset: listOffset,
      })
      if (generation !== workspaceGeneration || activeProjectId.value !== projectId) return
      if (Array.isArray(page)) {
        throw new Error('The source file total is unavailable. Reload and try again.')
      }
      const knownIds = new Set(files.value.map((file) => file.id))
      files.value.push(
        ...page.items.filter((file) => isCanonicalSourcePdf(file) && !knownIds.has(file.id)),
      )
      totalFiles.value = page.total
      listOffset += page.items.length
      hasMoreFiles.value = page.items.length > 0 && listOffset < page.total
    } catch (error) {
      if (generation === workspaceGeneration && activeProjectId.value === projectId)
        listError.value = normalizedError(error)
    } finally {
      if (generation === workspaceGeneration && activeProjectId.value === projectId)
        listLoading.value = false
    }
  }

  async function fetchFile(
    projectId: number,
    fileId: string,
    options: { includeWorkbook?: boolean; customerPortal?: boolean } = {},
  ): Promise<FileResourceOut | null> {
    beginProject(projectId)
    clearWorkbook()
    includeSelectedWorkbook = options.includeWorkbook ?? false
    selectedWorkbookCustomerVisible = options.customerPortal ?? false
    const generation = ++detailGeneration
    downloadGeneration += 1
    downloadingFileId.value = null
    downloadError.value = null
    if (selectedFile.value?.id !== fileId) {
      selectedFile.value =
        files.value.find((file) => file.id === fileId && isCanonicalSourcePdf(file)) ?? null
    }
    detailLoading.value = true
    detailError.value = null
    mutationError.value = null
    try {
      const resource = await ExtractionPublicationService.getFileV1ProjectsProjectIdFilesFileIdGet({
        projectId,
        fileId,
      })
      if (generation !== detailGeneration || activeProjectId.value !== projectId) return null
      if (!isCanonicalSourcePdf(resource)) {
        selectedFile.value = null
        detailError.value = unsupportedSourceFileError()
        return null
      }
      selectedFile.value = resource
      replaceResource(resource)
      void loadSelectedWorkbook()
      return resource
    } catch (error) {
      if (generation === detailGeneration && activeProjectId.value === projectId) {
        selectedFile.value = null
        detailError.value = normalizedError(error)
      }
      return null
    } finally {
      if (generation === detailGeneration && activeProjectId.value === projectId) {
        detailLoading.value = false
      }
    }
  }

  async function refreshAfterConflict(
    projectId: number,
    fileId: string,
    expectedMutationGeneration: number,
    expectedDetailGeneration: number,
  ): Promise<void> {
    try {
      const resource = await ExtractionPublicationService.getFileV1ProjectsProjectIdFilesFileIdGet({
        projectId,
        fileId,
      })
      if (
        expectedMutationGeneration !== mutationGeneration ||
        expectedDetailGeneration !== detailGeneration ||
        activeProjectId.value !== projectId ||
        selectedFile.value?.id !== fileId ||
        !isCanonicalSourcePdf(resource)
      )
        return
      const cachedRevision = files.value.find((file) => file.id === fileId)?.revision ?? -1
      const currentRevision = Math.max(selectedFile.value.revision, cachedRevision)
      if (resource.revision < currentRevision) return
      replaceResource(resource)
    } catch {
      // Keep the actionable mutation conflict visible. A normal detail reload
      // remains available if the best-effort revision refresh also fails.
    }
  }

  async function patchFile(
    projectId: number,
    fileId: string,
    patch: FilePatch,
  ): Promise<FileResourceOut> {
    beginProject(projectId)
    const generation = ++mutationGeneration
    const selectedGeneration = detailGeneration
    mutationLoading.value = true
    mutationError.value = null
    try {
      const resource =
        await ExtractionPublicationService.patchFileV1ProjectsProjectIdFilesFileIdPatch({
          projectId,
          fileId,
          requestBody: patch,
        })
      if (generation === mutationGeneration && activeProjectId.value === projectId) {
        replaceResource(resource)
      }
      return resource
    } catch (error) {
      const normalized = normalizedError(error)
      if (
        generation === mutationGeneration &&
        selectedGeneration === detailGeneration &&
        activeProjectId.value === projectId
      ) {
        if (selectedFile.value?.id === fileId) mutationError.value = normalized
        if (normalized.status === 409) {
          await refreshAfterConflict(projectId, fileId, generation, selectedGeneration)
        }
        if (selectedFile.value?.id !== fileId) mutationError.value = null
      }
      throw error
    } finally {
      if (generation === mutationGeneration && activeProjectId.value === projectId) {
        mutationLoading.value = false
      }
    }
  }

  async function downloadFile(projectId: number, file: FileResourceOut): Promise<void> {
    beginProject(projectId)
    const generation = ++downloadGeneration
    downloadingFileId.value = file.id
    downloadError.value = null
    try {
      const blob = requireBlob(
        await ExtractionPublicationService.getFileContentV1ProjectsProjectIdFilesFileIdContentGet({
          projectId,
          fileId: file.id,
        }),
      )
      if (
        generation === downloadGeneration &&
        activeProjectId.value === projectId &&
        selectedFile.value?.id === file.id
      ) {
        triggerBlobDownload(blob, file.name)
      }
    } catch (error) {
      if (
        generation === downloadGeneration &&
        activeProjectId.value === projectId &&
        selectedFile.value?.id === file.id
      ) {
        downloadError.value = normalizedError(error)
      }
      throw error
    } finally {
      if (generation === downloadGeneration && activeProjectId.value === projectId) {
        downloadingFileId.value = null
      }
    }
  }

  function clearSelection(): void {
    detailGeneration += 1
    downloadGeneration += 1
    selectedFile.value = null
    clearWorkbook()
    includeSelectedWorkbook = false
    detailLoading.value = false
    downloadingFileId.value = null
    detailError.value = null
    mutationError.value = null
    downloadError.value = null
  }

  function reset(): void {
    activeProjectId.value = null
    resetProjectState()
  }

  return {
    activeProjectId,
    files,
    totalFiles,
    scopes,
    selectedFile,
    scopeWorkbook,
    workbookLoading,
    workbookError,
    listLoading,
    hasMoreFiles,
    scopesLoading,
    detailLoading,
    mutationLoading,
    downloadingFileId,
    listError,
    scopesError,
    detailError,
    mutationError,
    downloadError,
    fetchWorkspace,
    loadMoreFiles,
    loadSelectedWorkbook,
    fetchFile,
    patchFile,
    downloadFile,
    clearSelection,
    reset,
  }
})
